<!-- components/LoadingBar.vue -->
<template>
  <div class="loading-bar-wrapper" v-if="loading">
    <div class="loading-bar-container">
      <div class="loading-header">
        <div class="loading-text">
          <div class="loading-spinner"></div>
          <span class="loading-label">Processing Configuration Analysis</span>
        </div>
        <span class="loading-percentage">{{ progressPercentage }}%</span>
      </div>

      <div class="progress-bar-track">
        <div
          class="progress-bar-fill"
          :style="{ width: progressPercentage + '%' }"
        >
          <div class="progress-bar-shine"></div>
        </div>
      </div>

      <div class="loading-status">
        <span class="status-text">{{ getStatusMessage() }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    progressPercentage: {
      type: Number,
      default: 0,
    },
  },
  methods: {
    getStatusMessage() {
      if (this.progressPercentage < 25) {
        return "Initialising comparison...";
      } else if (this.progressPercentage < 50) {
        return "Fetching configuration files...";
      } else if (this.progressPercentage < 75) {
        return "Analysing policies...";
      } else if (this.progressPercentage < 100) {
        return "Finalising results...";
      } else {
        return "Complete!";
      }
    },
  },
};
</script>

<style scoped>
.loading-bar-wrapper {
  padding: 2rem 0;
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.loading-bar-container {
  max-width: 600px;
  margin: 0 auto;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border-radius: 1rem;
  padding: 1.75rem 2rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.08),
    0 2px 4px -1px rgba(0, 0, 0, 0.04), inset 0 1px 0 0 rgba(255, 255, 255, 0.8);
  border: 1px solid #e2e8f0;
}

.loading-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.loading-text {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 3px solid #e2e8f0;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-label {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #334155;
  letter-spacing: -0.01em;
}

.loading-percentage {
  font-size: 1.125rem;
  font-weight: 700;
  color: #3b82f6;
  font-variant-numeric: tabular-nums;
  min-width: 48px;
  text-align: right;
}

/* Progress Bar Track */
.progress-bar-track {
  position: relative;
  height: 12px;
  background: linear-gradient(to bottom, #e2e8f0, #f1f5f9);
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.06);
}

/* Progress Bar Fill */
.progress-bar-fill {
  position: relative;
  height: 100%;
  background: linear-gradient(90deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%);
  border-radius: 1rem;
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 0 10px rgba(59, 130, 246, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

/* Animated shine effect */
.progress-bar-shine {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3) 50%,
    transparent
  );
  animation: shine 2s infinite;
}

@keyframes shine {
  0% {
    left: -100%;
  }
  50%,
  100% {
    left: 100%;
  }
}

/* Pulse animation when at 100% */
.progress-bar-fill[style*="100%"] {
  animation: pulse 1s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    box-shadow: 0 0 10px rgba(59, 130, 246, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
  }
  50% {
    box-shadow: 0 0 20px rgba(59, 130, 246, 0.5),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
  }
}

.loading-status {
  margin-top: 0.875rem;
  text-align: center;
}

.status-text {
  font-size: 0.8125rem;
  color: #64748b;
  font-style: italic;
  animation: fadeInOut 0.5s ease-in-out;
}

@keyframes fadeInOut {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Responsive */
@media (max-width: 640px) {
  .loading-bar-container {
    padding: 1.5rem 1.25rem;
  }

  .loading-label {
    font-size: 0.875rem;
  }

  .loading-percentage {
    font-size: 1rem;
  }
}
</style>
